import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { usePublicTheme } from '../../hooks/usePublicTheme';
import { DemoRequestModal, usePlatformDemo } from '../Vitrine/DemoRequestModal';
import { BrandLogo, Eyebrow, Pill, ThemeSwitch } from '../Vitrine/VitrineUi';
import { ACCOMPAGNEMENT, DOCUMENTS, MODULES, PROFILS, STATS } from '../Vitrine/vitrineData';
import type { Profil } from '../Vitrine/vitrineData';

/**
 * Vitrine publique (« / » hors connexion) — maquette « Accueil LIAmani » 2a de la passation :
 * papier blanc, encre noire, une seule couleur — celle de l'ambiance réglée par le super admin.
 * Remplace l'ancien site statique porté en Shadow DOM. Le bouton « Demander une démo » ouvre le
 * widget RDV Pro du super admin s'il l'a activé, sinon il n'apparaît pas.
 */
export const PublicHomepage: React.FC = () => {
    const { isDark, toggleTheme } = usePublicTheme();
    const { tokens } = useAmbiance();
    const demo = usePlatformDemo();
    const [demoOpen, setDemoOpen] = useState(false);
    const [profil, setProfil] = useState<Profil | 'all'>('resto');

    useEffect(() => {
        const previousTitle = document.title;
        document.title = "L'IAmani — La semaine, les renforts et la facture. Au même endroit.";
        return () => { document.title = previousTitle; };
    }, []);

    const modules = useMemo(() => MODULES.map((m) => ({ ...m, active: profil === 'all' || m.tags.includes(profil) })), [profil]);
    const demoButton = (size: 'md' | 'lg' = 'md') => demo.clientId
        ? <Pill size={size} onClick={() => setDemoOpen(true)}>{demo.label}</Pill>
        : null;

    return (
        <div className="min-h-screen bg-surface text-content" style={{ fontFamily: '"Instrument Sans", ui-sans-serif, system-ui, sans-serif' }}>
            {/* En-tête */}
            <header className="sticky top-0 z-40 bg-surface/85 backdrop-blur border-b border-border">
                <div className="max-w-[1160px] mx-auto px-5 h-[68px] flex items-center justify-between gap-4">
                    <div className="flex items-center gap-8 min-w-0">
                        <a href="#top" className="shrink-0"><BrandLogo className="h-6" /></a>
                        <nav className="hidden md:flex gap-7 text-[14px] font-medium text-content-muted">
                            <a href="#modules" className="hover:text-content">Modules</a>
                            <a href="#metiers" className="hover:text-content">Métiers</a>
                            <a href="#accompagnement" className="hover:text-content">Accompagnement</a>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        <ThemeSwitch isDark={isDark} onToggle={toggleTheme} />
                        <span className="hidden sm:block"><Pill variant="ghost" to="/login">Se connecter</Pill></span>
                        {demoButton()}
                    </div>
                </div>
            </header>

            <main id="top">
                {/* Bande d'ambiance + accroche */}
                {/* Voile plus léger et uniforme que celui de l'en-tête de l'app : le texte est centré, et le
                    ciel étoilé doit rester net derrière lui (retour d'Emeni du 15/09). */}
                <AmbianceBand veil="linear-gradient(180deg, rgba(5,6,10,.30) 0%, rgba(5,6,10,.18) 60%, rgba(5,6,10,.45) 100%)">
                    <section className="max-w-[1160px] mx-auto px-5 pt-16 md:pt-20 text-center">
                        <h1 className="mx-auto max-w-[780px] text-[38px] md:text-[62px] font-semibold leading-[1.05] tracking-[-0.02em] mb-5">
                            La semaine, les renforts et la facture. Au même endroit.
                        </h1>
                        <p className="mx-auto max-w-[560px] text-[16px] md:text-[17px] text-content-muted leading-relaxed mb-7">
                            Le logiciel de gestion des brasseries et des prestataires de service. Vous activez vos modules, on développe ceux qui vous manquent.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            {demoButton('lg')}
                            <Pill variant="secondary" size="lg" to="/login">Accéder à mon espace</Pill>
                        </div>
                        {demo.clientId && <p className="mt-4 text-[12.5px] text-content-muted">30 minutes, sans engagement — on configure votre espace avec vous.</p>}

                        {/* Capture produit dans un cadre de navigateur, avec une carte flottante */}
                        <div className="relative mx-auto mt-12 max-w-[980px]">
                            <div className="rounded-t-[14px] border border-b-0 border-border bg-surface overflow-hidden shadow-2xl">
                                <div className="flex items-center gap-1.5 h-9 px-3 border-b border-border bg-surface-soft">
                                    <span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" />
                                    <span className="ml-3 text-[11px] font-mono text-content-muted">l-iamani.com/planning</span>
                                </div>
                                <img src="/vitrine/planning.png" alt="Grille de planning de la semaine, un poste par ligne" className="block w-full h-auto" />
                            </div>
                            <div className="hidden md:block absolute -left-6 top-[38%] w-[230px] text-left rounded-xl border border-border bg-surface p-4 shadow-2xl">
                                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2">Serveurs · samedi</div>
                                <div className="flex items-end gap-4">
                                    <div><span className="text-[26px] font-bold leading-none">6</span> <span className="text-[12px] text-content-muted">postes à couvrir</span></div>
                                </div>
                                <div className="flex items-end gap-4 mt-1">
                                    <div><span className="text-[26px] font-bold leading-none" style={{ color: tokens.acc }}>4</span> <span className="text-[12px] text-content-muted">renforts à commander</span></div>
                                </div>
                                <div className="mt-3 h-1.5 rounded-full bg-surface-soft overflow-hidden"><div className="h-full w-1/3 rounded-full" style={{ background: tokens.acc }} /></div>
                                <div className="mt-2 text-[11.5px] text-content-muted">2 déjà affectés en interne</div>
                            </div>
                        </div>
                    </section>
                </AmbianceBand>

                {/* Chiffres vérifiables */}
                <section className="border-y border-border bg-surface">
                    <div className="max-w-[1160px] mx-auto px-5 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
                        {STATS.map((s) => (
                            <div key={s.label} className="py-6 md:px-6 first:pl-0">
                                <div className="text-[28px] font-bold tracking-tight leading-none">{s.value}</div>
                                <div className="mt-2 text-[13px] text-content-muted">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Modulable */}
                <section id="modules" className="max-w-[1160px] mx-auto px-5 py-16 md:py-20">
                    <div id="metiers" className="flex flex-wrap items-end justify-between gap-6 mb-8">
                        <div className="max-w-[520px]">
                            <Eyebrow>Modulable</Eyebrow>
                            <h2 className="text-[30px] md:text-[36px] font-semibold leading-[1.1] tracking-[-0.02em] mb-3">Choisissez votre métier. On éteint le reste.</h2>
                            <p className="text-[14.5px] text-content-muted">Votre espace ne montre que vos modules. Pas d'écran inutile, pas de champ qui ne vous concerne pas.</p>
                        </div>
                        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Métier">
                            {PROFILS.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={profil === p.key}
                                    onClick={() => setProfil(p.key)}
                                    className={`h-[38px] px-4 rounded-full text-[13px] font-semibold whitespace-nowrap shrink-0 border transition-colors ${profil === p.key ? 'bg-content text-surface border-content' : 'bg-surface text-content border-border hover:border-content'}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border rounded-xl overflow-hidden">
                        {modules.map((m) => (
                            <div key={m.name} className={`bg-surface p-4 transition-colors hover:bg-surface-soft/60 ${m.active ? '' : 'opacity-60'}`}>
                                <div className="flex items-center gap-1.5 mb-2">
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.active ? tokens.acc : 'var(--text-muted)' }} />
                                    <span className="text-[10px] font-bold uppercase tracking-[.09em]" style={{ color: m.active ? tokens.acc : 'var(--text-muted)' }}>{m.active ? 'Activé' : 'Disponible'}</span>
                                </div>
                                <h3 className="text-[14px] font-semibold leading-tight mb-1">{m.name}</h3>
                                <p className="text-[12px] text-content-muted leading-snug">{m.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Concrètement */}
                <section className="bg-surface-app border-y border-border">
                    <div className="max-w-[1160px] mx-auto px-5 py-16 md:py-20">
                        <Eyebrow>Concrètement</Eyebrow>
                        <h2 className="max-w-[560px] text-[30px] md:text-[36px] font-semibold leading-[1.1] tracking-[-0.02em] mb-10">Trois documents que vous ne referez plus à la main.</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {DOCUMENTS.map((d) => (
                                <article key={d.title} className="rounded-xl border border-border bg-surface overflow-hidden transition-transform hover:-translate-y-0.5">
                                    <div className="aspect-[4/3] bg-surface-soft overflow-hidden">
                                        <img src={d.src} alt={d.alt} className="w-full h-full object-cover object-top" loading="lazy" />
                                    </div>
                                    <div className="p-5">
                                        <h3 className="text-[15px] font-semibold mb-1.5">{d.title}</h3>
                                        <p className="text-[13px] text-content-muted leading-relaxed">{d.desc}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Accompagnement */}
                <section id="accompagnement" className="max-w-[1160px] mx-auto px-5 py-16 md:py-20">
                    <div className="rounded-2xl border border-border bg-surface p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                            <Eyebrow>Accompagnement</Eyebrow>
                            <h2 className="text-[28px] md:text-[32px] font-semibold leading-[1.1] tracking-[-0.02em] mb-4">Une équipe qui code ce qui vous manque.</h2>
                            <p className="text-[14.5px] text-content-muted leading-relaxed">Un outil vous manque ? Vous l'écrivez depuis le bouton support de votre espace. Une vraie personne répond, et la fonctionnalité est développée pour votre établissement.</p>
                        </div>
                        <ul className="divide-y divide-border self-center">
                            {ACCOMPAGNEMENT.map((a) => (
                                <li key={a.label} className="flex flex-wrap items-baseline justify-between gap-3 py-3.5">
                                    <span className="text-[14px] font-semibold">{a.label}</span>
                                    <span className="text-[12.5px] text-content-muted">{a.note}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Appel final */}
                <section className="max-w-[1160px] mx-auto px-5 pb-20 text-center">
                    <h2 className="text-[30px] md:text-[40px] font-semibold leading-[1.1] tracking-[-0.02em] mb-3">On configure votre espace avec vous.</h2>
                    <p className="mx-auto max-w-[460px] text-[14.5px] text-content-muted mb-7">Une démo de 30 minutes, vos modules choisis ensemble, votre équipe formée.</p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {demoButton('lg')}
                        <Pill variant="secondary" size="lg" to="/login">Se connecter</Pill>
                    </div>
                </section>
            </main>

            <footer className="border-t border-border">
                <div className="max-w-[1160px] mx-auto px-5 h-[72px] flex flex-wrap items-center justify-between gap-4">
                    <BrandLogo className="h-5 opacity-60" />
                    <nav className="flex gap-6 text-[12.5px] text-content-muted">
                        <Link to="/legal" className="hover:text-content">Mentions légales</Link>
                        <Link to="/privacy" className="hover:text-content">Confidentialité</Link>
                        <Link to="/login" className="hover:text-content">Se connecter</Link>
                    </nav>
                </div>
            </footer>

            {demo.clientId && <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} clientId={demo.clientId} title={demo.label} />}
        </div>
    );
};

export default PublicHomepage;
